import { useEffect, useMemo, useState } from "react";
import { Segmented, Typography } from "antd";
import { CommentOutlined, DashboardOutlined } from "../icons";
import type { DispatchDetectionReport } from "../../../shared/dispatchTypes";
import { DispatchAlignedBoard } from "./DispatchAlignedBoard";
import { DispatchChatBoard } from "./DispatchChatBoard";
import {
  DispatchMediaPreviewModal,
  DispatchRoomDetailModal,
  buildAlignedRows,
  useDispatchBoardModals,
  type LoadRoomDetails,
} from "./dispatchBoardShared";

const { Text } = Typography;

export type DispatchBoardMode = "chat" | "timeline";

export const DISPATCH_BOARD_MODE_KEY = "dispatchMonitorBoardMode";
/** 默认进入聊天界面（微信式会话流） */
export const DEFAULT_DISPATCH_BOARD_MODE: DispatchBoardMode = "chat";

function readStoredMode(): DispatchBoardMode {
  const stored = localStorage.getItem(DISPATCH_BOARD_MODE_KEY);
  return stored === "chat" || stored === "timeline" ? stored : DEFAULT_DISPATCH_BOARD_MODE;
}

/**
 * 发单监控看板容器：持有视图模式与两个弹窗，具体渲染交给时间线 / 聊天界面。
 * `loadRoomDetails` 只给 mock 预览用，线上走默认的 tRPC 查询。
 */
export function DispatchBoardView({
  report,
  loadRoomDetails,
}: {
  report: DispatchDetectionReport;
  loadRoomDetails?: LoadRoomDetails;
}) {
  const [mode, setMode] = useState<DispatchBoardMode>(readStoredMode);
  const rows = useMemo(() => buildAlignedRows(report), [report]);
  const modals = useDispatchBoardModals(report, loadRoomDetails);

  useEffect(() => {
    localStorage.setItem(DISPATCH_BOARD_MODE_KEY, mode);
  }, [mode]);

  if (rows.length === 0) {
    return (
      <div className="dispatch-board-empty">
        当前时间窗口内没有可对齐的消息。请确认业务单元已配置源群、采集群和目标群。
      </div>
    );
  }

  return (
    <>
      <div className="dispatch-board-modes">
        <Segmented
          value={mode}
          onChange={(value) => setMode(value as DispatchBoardMode)}
          options={[
            { label: "聊天界面", value: "chat", icon: <CommentOutlined /> },
            { label: "时间线", value: "timeline", icon: <DashboardOutlined /> },
          ]}
        />
        <Text type="secondary" className="dispatch-board-modes__hint">
          {mode === "chat"
            ? "仿会话流：源头群 / 采集群在左侧，发单群在右侧，点击覆盖率查看群命中详情。"
            : "分列对照：源头群 / 采集群 / 发单群按链路左右并排。"}
        </Text>
      </div>

      {mode === "chat" ? (
        <DispatchChatBoard
          report={report}
          rows={rows}
          onOpenRooms={modals.openTargetRooms}
          onPreview={modals.openMediaPreview}
        />
      ) : (
        <DispatchAlignedBoard
          report={report}
          rows={rows}
          onOpenRooms={modals.openTargetRooms}
          onPreview={modals.openMediaPreview}
        />
      )}

      <DispatchRoomDetailModal
        state={modals.roomModal}
        loading={modals.roomModalLoading}
        onClose={modals.closeRoomModal}
      />
      <DispatchMediaPreviewModal preview={modals.mediaModal} onClose={modals.closeMediaPreview} />
    </>
  );
}
